import React from "react";
import getSharedAudioContext from "./audioContext";
import { getBeatsPerMeasure } from "../utils/timing";

const SCHEDULE_AHEAD_SEC = 1.0;
const TICK_INTERVAL_MS = 100;
const DEFAULT_VOLUME = 1;
export const DEFAULT_CLICK_PRESET = "click1";
export const CLICK_PRESETS = {
  click1: { label: "Click 1", waveform: "sine", normalFrequency: 1800, accentFrequency: 2400, duration: 0.08, attack: 0.005, highpass: 200, normalLevel: 1, accentLevel: 1 },
  click2: { label: "Click 2", waveform: "sine", normalFrequency: 700, accentFrequency: 1400, duration: 0.04, attack: 0.005, highpass: 0, normalLevel: 1, accentLevel: 1 },
  click3: { label: "Click 3", waveform: "triangle", normalFrequency: 1200, accentFrequency: 2100, duration: 0.055, attack: 0.003, highpass: 180, normalLevel: 1.2, accentLevel: 1.3 },
};
const DEFAULT_CLICK_SOUND = CLICK_PRESETS[DEFAULT_CLICK_PRESET];

export function useClickTrack({ beatClock, measureBoundaries, accentGate, beatOffset = 0, suppressAccent = false, clickSound = DEFAULT_CLICK_SOUND }) {
  const [isClickPlaying, setIsClickPlaying] = React.useState(false);
  const [volume, setVolume] = React.useState(DEFAULT_VOLUME);
  const [pan, setPan] = React.useState(-1);
  const ctxRef = React.useRef(null);
  const schedulerRef = React.useRef(null);
  const nextBeatIndexRef = React.useRef(0);
  const scheduledSourcesRef = React.useRef([]);
  const measureBoundarySetRef = React.useRef(new Set());
  const accentGateRef = React.useRef({ muteUntilBeat: null });
  const beatOffsetRef = React.useRef(0);
  const clickSoundRef = React.useRef(DEFAULT_CLICK_SOUND);
  const debugEnabled = React.useCallback(() => {
    try {
      return window?.__ctDebugClick === true || localStorage.getItem("ct-debug-click") === "1";
    } catch (err) {
      return false;
    }
  }, []);
  const masterGainRef = React.useRef(null);
  const panNodeRef = React.useRef(null);
  const stopClickRef = React.useRef(null);

  React.useEffect(() => {
    if (!measureBoundaries?.boundarySet) {
      measureBoundarySetRef.current = new Set();
      return;
    }
    measureBoundarySetRef.current = new Set(measureBoundaries.boundarySet);
  }, [measureBoundaries]);

  React.useEffect(() => {
    accentGateRef.current = accentGate || { muteUntilBeat: null };
  }, [accentGate]);

  React.useEffect(() => {
    beatOffsetRef.current = Number.isFinite(beatOffset) ? beatOffset : 0;
  }, [beatOffset]);

  React.useEffect(() => {
    clickSoundRef.current = { ...DEFAULT_CLICK_SOUND, ...(clickSound || {}) };
  }, [clickSound]);

  const ensureCtx = React.useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = getSharedAudioContext();
    }
    if (!masterGainRef.current || !panNodeRef.current) {
      const ctx = ctxRef.current;
      const gain = ctx.createGain();
      const panNode = ctx.createStereoPanner();
      gain.gain.value = volume;
      panNode.pan.value = pan;
      gain.connect(panNode).connect(ctx.destination);
      masterGainRef.current = gain;
      panNodeRef.current = panNode;
    }
    return ctxRef.current;
  }, [pan, volume]);

  const scheduleBeat = React.useCallback(
    (time, isAccent) => {
      const ctx = ensureCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const sound = clickSoundRef.current;
      const duration = Math.max(0.01, sound.duration);
      const attack = Math.min(Math.max(0.001, sound.attack), duration * 0.8);
      const endTime = time + duration;

      osc.type = sound.waveform;
      osc.frequency.value = isAccent ? sound.accentFrequency : sound.normalFrequency;

      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.exponentialRampToValueAtTime(isAccent ? sound.accentLevel : sound.normalLevel, time + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, endTime);

      const masterGain = masterGainRef.current;
      const panNode = panNodeRef.current;
      osc.connect(gain);
      if (sound.highpass > 0) {
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = sound.highpass;
        filter.Q.value = 0.7;
        gain.connect(filter).connect(masterGain || ctx.destination);
      } else {
        gain.connect(masterGain || ctx.destination);
      }
      if (masterGain && panNode && !masterGain.numberOfOutputs) {
        // fallback guard; but normally masterGain connects to pan in ensureCtx
        masterGain.connect(panNode).connect(ctx.destination);
      }
      osc.start(time);
      osc.stop(endTime + 0.01);
      scheduledSourcesRef.current.push({ osc, stopAt: endTime + 0.01 });
    },
    [ensureCtx],
  );

  const shouldAccentBeat = React.useCallback(
    (beatIndex) => {
      if (suppressAccent) return false;
      const timelineBeat = beatIndex + (beatOffsetRef.current || 0);
      const muteUntilBeat = accentGateRef.current?.muteUntilBeat;
      if (Number.isFinite(muteUntilBeat) && timelineBeat < muteUntilBeat) return false;
      if (measureBoundarySetRef.current.size) {
        return measureBoundarySetRef.current.has(timelineBeat);
      }
      const beatsPerMeasure = getBeatsPerMeasure(beatClock?.beatsPerMeasure);
      return beatIndex % beatsPerMeasure === 0;
    },
    [beatClock?.beatsPerMeasure, suppressAccent],
  );

  const scheduleBeats = React.useCallback(() => {
    const ctx = ensureCtx();
    const secondsPerBeat = beatClock?.secondsPerBeat;
    if (!Number.isFinite(secondsPerBeat) || secondsPerBeat <= 0) {
      if (debugEnabled()) {
        console.log("[click-debug] skip schedule: invalid secondsPerBeat", secondsPerBeat);
      }
      return;
    }
    if (!beatClock?.isAnchored?.()) {
      if (debugEnabled()) {
        console.log("[click-debug] skip schedule: no anchor");
      }
      return;
    }
    const lookAheadEnd = ctx.currentTime + SCHEDULE_AHEAD_SEC;

    if (scheduledSourcesRef.current.length) {
      const now = ctx.currentTime;
      scheduledSourcesRef.current = scheduledSourcesRef.current.filter((entry) => entry.stopAt > now - 0.1);
    }

    const currentBeat = beatClock.timeToBeat(ctx.currentTime);
    if (Number.isFinite(currentBeat)) {
      const minBeat = Math.ceil(currentBeat - 0.0001);
      if (nextBeatIndexRef.current < minBeat) {
        nextBeatIndexRef.current = minBeat;
      }
    }

    while (true) {
      const beatIndex = nextBeatIndexRef.current;
      const beatTime = beatClock.beatToTime(beatIndex);
      if (!Number.isFinite(beatTime) || beatTime >= lookAheadEnd) break;
      const isAccent = shouldAccentBeat(beatIndex);
      if (isAccent && debugEnabled()) {
        console.log("[click-debug] accent", {
          beatIndex,
          timelineBeat: beatIndex + (beatOffsetRef.current || 0),
          beatTime,
        });
      }
      scheduleBeat(beatTime, isAccent);
      nextBeatIndexRef.current += 1;
    }
  }, [beatClock, debugEnabled, ensureCtx, scheduleBeat, shouldAccentBeat]);

  const startClick = React.useCallback(
    async (startAt, startBeat = 0, beatOffsetOverride = null) => {
      if (isClickPlaying) return;
      if (schedulerRef.current) {
        clearInterval(schedulerRef.current);
        schedulerRef.current = null;
      }
      const ctx = ensureCtx();
      if (debugEnabled()) {
        console.log("[click-debug] startClick", {
          audioState: ctx.state,
          startAt,
          startBeat,
        });
      }
      try {
        await ctx.resume?.();
      } catch (_) {
        // ignore resume errors
      }
      const safeStartAt = typeof startAt === "number" && startAt > ctx.currentTime ? startAt : ctx.currentTime + 0.1;
      beatClock?.resetAnchor?.();
      beatClock?.setAnchor?.(safeStartAt, startBeat);
      if (Number.isFinite(beatOffsetOverride)) {
        beatOffsetRef.current = beatOffsetOverride;
      }
      nextBeatIndexRef.current = Number.isFinite(startBeat) ? startBeat : 0;
      if (debugEnabled()) {
        console.log("[click-debug] anchored", {
          safeStartAt,
          anchorAt: beatClock?.anchorAtRef?.current,
          anchorBeat: beatClock?.anchorBeatRef?.current,
          beatOffset: beatOffsetRef.current,
        });
      }

      scheduleBeats();
      schedulerRef.current = setInterval(scheduleBeats, TICK_INTERVAL_MS);
      setIsClickPlaying(true);
    },
    [beatClock, ensureCtx, isClickPlaying, scheduleBeats],
  );

  const stopClick = React.useCallback(() => {
    if (schedulerRef.current) {
      clearInterval(schedulerRef.current);
      schedulerRef.current = null;
    }
    scheduledSourcesRef.current.forEach(({ osc }) => {
      try {
        osc.stop(0);
      } catch (_) {
        // ignore stop errors
      }
    });
    scheduledSourcesRef.current = [];
    setIsClickPlaying(false);
    beatClock?.resetAnchor?.();
    if (debugEnabled()) {
      console.log("[click-debug] stopClick");
    }
  }, [beatClock]);

  React.useEffect(() => {
    stopClickRef.current = stopClick;
  }, [stopClick]);

  React.useEffect(() => () => {
    stopClickRef.current?.();
  }, []);

  const setClickVolume = React.useCallback(
    (val) => {
      setVolume(val);
      if (masterGainRef.current) {
        const ctx = ensureCtx();
        const now = ctx.currentTime;
        masterGainRef.current.gain.cancelScheduledValues(now);
        masterGainRef.current.gain.setValueAtTime(masterGainRef.current.gain.value, now);
        masterGainRef.current.gain.linearRampToValueAtTime(val, now + 0.05);
      }
    },
    [ensureCtx],
  );

  const setClickPan = React.useCallback((val) => {
    setPan(val);
    if (panNodeRef.current) {
      panNodeRef.current.pan.value = val;
    }
  }, []);

  return {
    isClickPlaying,
    startClick,
    stopClick,
    volume,
    setClickVolume,
    pan,
    setClickPan,
  };
}

export default useClickTrack;
